import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
import {
	addDoc,
	collection,
	doc,
	getDocs,
	query,
	setDoc,
	Timestamp,
	updateDoc,
	where,
} from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { queryByBrokerGroup } from '@/utils/firebaseService';
import { safelyParseDate } from '@/utils/date';
import { getActingUser } from '../user/userApi';
import { getCreateUrlFromPipeline, getUrlFromPipeline } from '@/utils/constants';
import { ticketsApi } from '../claims/ticketsApi';

const STAGES_COLLECTION = 'stages';
const PIPELINE_COLLECTION = 'pipeline';
const STAGE_LABELS_COLLECTION = 'stageLabels';

export const PIPELINES = [
	process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
	process.env.REACT_APP_PIPELINE_RENEWALS,
	process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
	process.env.REACT_APP_PIPELINE_CLAIMS,
];

const mapBrokerGroup = (ids) => ids.map((id) => doc(collection(firestore, 'brokerGroup'), id));

const normaliseStageLabel = (d) => ({
	...JSON.parse(JSON.stringify(d.data())),
	id: d.id,
	docId: d.id,
	brokerGroupIds: (d.data().brokerGroups ?? []).map((b) => b.id),
	createdWhen: safelyParseDate(d.data().createdWhen)?.getTime?.(),
	lastUpdated: safelyParseDate(d.data().lastUpdated)?.getTime?.(),
});

const listFirebasePipelines = async (params) => {
	const pipelineName = params?.pipeline;
	try {
		if (pipelineName) {
			const pipelineQuery = query(
				collection(firestore, PIPELINE_COLLECTION),
				where('name', '==', pipelineName).limit(1),
			);
			const pipelineSnapshot = await getDocs(pipelineQuery);
			const pipelineDoc = pipelineSnapshot?.docs[0];

			if (pipelineSnapshot.empty) {
				console.log(`Pipeline ${pipelineName} does not exist`);
				return [];
			}

			return await getPipelineStages(pipelineDoc);
		} else {
			const pipelineQuery = query(collection(firestore, PIPELINE_COLLECTION));
			const pipelinesSnapshot = await getDocs(pipelineQuery);

			const result = await Promise.all(
				pipelinesSnapshot.docs.map(async (doc) => {
					const res = await getPipelineStages(doc).catch((e) =>
						console.error(`Error getting pipeline stages ${doc.id}`, e),
					);
					return res;
				}),
			).then((r) => r.filter(Boolean));

			const stages = result.flatMap((r) => r.stages);

			const active = stages.filter((s) => s.active);
			const inactive = stages.filter((s) => s.inactive);
			const closed = stages.filter((s) => s.closed);
			const completed = stages.filter((s) => s.completed);
			const endorsements = stages.filter((s) => s.isEndorsement);

			return {
				pipelines: result,
				stages,
				active,
				inactive,
				closed,
				completed,
				endorsements,
			};
		}
	} catch (error) {
		console.error(`Error: ${error?.message}`);
		return [];
	}
};

const getPipelineStages = async (pipelineDoc) => {
	const stagesQuery = query(
		collection(firestore, STAGES_COLLECTION),
		where('pipelineId', '==', pipelineDoc?.id),
	);
	const stages = await getDocs(stagesQuery);

	return {
		pipelineId: pipelineDoc.id,
		pipelineLabel: pipelineDoc.data().name,
		stages: stages.docs.map(mapFirebaseStage).sort((a, b) => a.order - b.order),
	};
};

const mapFirebaseStage = (doc) => {
	const name = doc.data().name;
	const id = doc.id;
	const pipelineId = doc?.data()?.pipelineId;
	const closed = Boolean(doc.data().closed);
	const completed = Boolean(doc.data().completed);

	return {
		stageId: id,
		id,
		stageName: name,
		name,
		label: name,
		order: doc?.data()?.order,
		pipelineId,
		closed,
		completed,
		active: !closed,
		inactive: completed,
		isEndorsement:
			name?.toLowerCase().includes('endorsement') ||
			pipelineId === process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
	};
};

// Creating an API service using RTK Query
export const pipelineApi = createApi({
	reducerPath: 'pipelineApi',
	tagTypes: ['PIPELINES', 'STAGE_LABELS'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	keepUnusedDataFor: 900, // Cache will only clear after 900 seconds (15 minutes)
	endpoints: (builder) => ({
		getStageLabels: builder.query({
			providesTags: ['STAGE_LABELS'],
			queryFn: async (
				{ pipelineId, stageId, brokerGroupIds },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const docs = await queryByBrokerGroup({
						collectionRef: collection(firestore, STAGE_LABELS_COLLECTION),
						// Plain ids (not the brokerGroups refs), which the Firestore rule checks.
						brokerGroupConstraint: {
							fieldPath: 'brokerGroupIds',
							optString: 'array-contains-any',
						},
						brokerGroupIds,
						...((stageId || pipelineId) && {
							constraints: [
								...(stageId ? [where('stageId', '==', stageId)] : []),
								...(pipelineId ? [where('pipelineId', '==', pipelineId)] : []),
							],
						}),
					});

					const data = docs.map(normaliseStageLabel);

					return {
						data,
					};
				} catch (error) {
					console.error('❌ Error retrieving pipelines and stages', error);

					return {
						error,
					};
				}
			},
		}),
		getCombinedPipelinesAndStages: builder.query({
			providesTags: ['PIPELINES'],
			queryFn: async (
				{ brokerGroupIds },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const userData = await getActingUser(dispatch, getState);
					const ledger = userData?.broker?.ledger?.split?.('__')?.[0]?.trim?.();

					const firebaseData = await listFirebasePipelines();

					const active = firebaseData.active;
					const inactive = firebaseData.inactive;
					const closed = firebaseData.closed;
					const completed = firebaseData.completed ?? [];
					const endorsements = firebaseData.endorsements;

					const firebasePipelines = firebaseData.pipelines;
					const allFirebaseStages = firebaseData.stages;

					const combinedPipelines = firebasePipelines
						.map((p) => {
							const name = p.pipelineLabel ?? '';
							return {
								id: p.pipelineId,
								pipelineName: name,
								label: name ? name.charAt(0).toUpperCase() + name.slice(1) : '',
							};
						})
						.map((p) => ({
							...p,
							url: getUrlFromPipeline(p.id),
							createUrl: getCreateUrlFromPipeline(p.id),
						}));

					const allPipelineIds = Array.from(
						new Set([...PIPELINES, ...firebasePipelines.map((p) => p.pipelineId)]),
					);

					const labelsByPipeline = await Promise.all(
						allPipelineIds.map((pipelineId) =>
							dispatch(
								pipelineApi.endpoints.getStageLabels.initiate({
									brokerGroupIds,
									pipelineId,
								}),
							).unwrap(),
						),
					);
					const labels = labelsByPipeline.flat();

					const claimStatuses = await dispatch(
						ticketsApi.endpoints.retrieveFirebaseClaimStatus.initiate({ ledger }),
					).unwrap();

					const completedStage = claimStatuses?.find((s) => s.isClosedStage);

					const stagesWithClaimStatusOverride = allFirebaseStages.map((s) => ({
						...s,
						...(s.id === completedStage?.hubspotStageId &&
							s.pipelineId === process.env.REACT_APP_PIPELINE_CLAIMS && {
								completed: true,
							}),
					}));

					const combinedStages = stagesWithClaimStatusOverride.map((s) => {
						const labelData = labels.find(
							(label) =>
								label.pipelineId === s.pipelineId &&
								[s.id, s.stageId].filter(Boolean).includes(label.stageId),
						);
						const label = labelData?.label ?? s.label ?? s.name;
						return {
							...s,
							stageId: s.stageId ?? s.id,
							isFinalStage: s.completed,
							label,
							name: label,
							defaultLabel: s.label ?? s.name,
							labelData,
							order: s.order ?? s.displayOrder ?? 0,
							displayOrder: s.order ?? s.displayOrder ?? 0,
						};
					});

					console.log('Combined stages', combinedStages);

					const baseContext = {
						pipelines: combinedPipelines,
						stages: combinedStages,
						active,
						inactive,
						closed,
						completed,
						endorsements,
						firebasePipelines,
					};

					const mapped = allPipelineIds.reduce(
						(acc, pipelineId) => {
							acc[pipelineId] = {
								...baseContext,
								pipeline: combinedPipelines.find((p) => p.id === pipelineId),
								stages: combinedStages.filter((s) => s?.pipelineId === pipelineId),
								active: active
									.filter((s) => s?.pipelineId === pipelineId)
									.map((s) => s?.id),
								activeStages: active.filter((s) => s?.pipelineId === pipelineId),
								inactive: inactive
									.filter((s) => s?.pipelineId === pipelineId)
									.map((s) => s?.id),
								closed: closed
									.filter((s) => s?.pipelineId === pipelineId)
									.map((s) => s?.id),
								completed: completed
									.filter((s) => s?.pipelineId === pipelineId)
									.map((s) => s?.id),
								endorsements: endorsements.map((s) => s?.id),
							};
							return acc;
						},
						{ undefined: baseContext },
					);
					return { data: mapped };
				} catch (error) {
					console.error(`Error: ${error?.message}`);

					const baseContext = {
						pipelines: PIPELINES.slice(),
						stages: [],
						active: [],
						inactive: [],
						closed: [],
						completed: [],
						endorsements: [],
						firebasePipelines: [],
					};

					const errorRes = PIPELINES.reduce(
						(acc, pipelineId) => {
							acc[pipelineId] = {
								...baseContext,
								pipeline: { id: pipelineId },
							};
							return acc;
						},
						{ undefined: baseContext },
					);

					return {
						data: errorRes,
					};
				}
			},
		}),
		saveStageLabel: builder.mutation({
			invalidatesTags: ['STAGE_LABELS', 'PIPELINES'],
			queryFn: async (
				{ stageId, pipelineId, label },
				{ dispatch, getState },
				options,
				baseQuery,
			) => {
				const collectionRef = collection(firestore, STAGE_LABELS_COLLECTION);

				const userData = await getActingUser(dispatch, getState);

				const brokerGroupIds = userData?.brokerGroupArray ?? [];
				const stageLabels = await dispatch(
					pipelineApi.endpoints.getStageLabels.initiate({
						brokerGroupIds,
						pipelineId,
						stageId,
					}),
				).unwrap();

				const existing = stageLabels?.[0];

				console.log('Existing', existing);

				try {
					const body = {
						label,
						stageId,
						pipelineId,
						lastUpdated: Timestamp.now(),
						lastUpdatedBy: userData.userId,
					};

					if (existing) {
						const existingDoc = doc(collectionRef, existing.docId);

						const existingIds = Array.from(
							new Set((existing.brokerGroupIds ?? []).concat(brokerGroupIds)),
						).filter(Boolean);
						const brokerGroups = mapBrokerGroup(existingIds);

						const updateDocBody = {
							...body,
							brokerGroups,
							brokerGroupIds: existingIds,
						};
						await updateDoc(existingDoc, updateDocBody);

						return {
							data: {
								...JSON.parse(JSON.stringify(updateDocBody)),
								id: existingDoc.id,
								docId: existingDoc.id,
								createdWhen: safelyParseDate(updateDocBody)?.getTime?.(),
								lastUpdated: safelyParseDate(updateDocBody)?.getTime?.(),
							},
						};
					} else {
						const newDoc = doc(collectionRef);

						const brokerGroups = mapBrokerGroup(brokerGroupIds);

						const newDocBody = {
							...body,
							brokerGroupIds,
							brokerGroups,
							createdWhen: Timestamp.now(),
							createdBy: userData.userId,
						};
						await setDoc(newDoc, newDocBody);

						return {
							data: {
								...JSON.parse(JSON.stringify(newDocBody)),
								id: newDoc.id,
								docId: newDoc.id,
								createdWhen: safelyParseDate(newDocBody)?.getTime?.(),
								lastUpdated: safelyParseDate(newDocBody)?.getTime?.(),
							},
						};
					}
				} catch (err) {
					console.log('Error saving deal stage label', err);
					return {
						error: {
							status: 500,
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
			transformResponse: (response) => {
				console.log('saveFormConfig', response);
				return response;
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetStageLabelsQuery,
	useGetCombinedPipelinesAndStagesQuery,
	useSaveStageLabelMutation,
} = pipelineApi;
